import { get, authGet, authPost, getTeamsWithStreams } from './apiService';

/**
 * Pick a sensible default sprint based on the current date (sprint date range),
 * not creation time. Prefer: 1) sprint containing today, 2) most recently ended
 * sprint, 3) next upcoming sprint, 4) first in the list.
 * @param {Array<{ sprintName: string, startDate: string|Date, endDate: string|Date }>} sprints
 * @returns {string|null} sprintName to use as default
 */
export const getDefaultSprintByCurrentDate = (sprints) => {
  if (!sprints || sprints.length === 0) return null;
  const nowTime = new Date().getTime();

  let currentSprint = null;
  let bestPastSprint = null;
  let bestPastEndTime = -Infinity;
  let bestFutureSprint = null;
  let bestFutureStartTime = Infinity;

  for (const sprint of sprints) {
    const start = new Date(sprint.startDate).getTime();
    const end = new Date(sprint.endDate).getTime();
    if (nowTime >= start && nowTime <= end) {
      currentSprint = sprint;
      break;
    }
    if (end < nowTime && end > bestPastEndTime) {
      bestPastEndTime = end;
      bestPastSprint = sprint;
    }
    if (start > nowTime && start < bestFutureStartTime) {
      bestFutureStartTime = start;
      bestFutureSprint = sprint;
    }
  }

  const chosen = currentSprint || bestPastSprint || bestFutureSprint || sprints[0];
  return chosen ? chosen.sprintName : null;
};

/**
 * Flatten a streamMap (as returned by getStreamMap) into a sorted, deduplicated
 * list of team IDs. Use this to populate Team dropdowns so they stay in sync
 * with the Team Management config.
 */
export const flattenTeams = (streamMap = {}) => {
  const ids = new Set();
  Object.values(streamMap).forEach(teams => {
    (teams || []).forEach(t => { if (t && t.id) ids.add(t.id); });
  });
  return Array.from(ids).sort();
};

/**
 * Fetch iteration-level metrics for a single team.
 */
export const getIterationMetrics = async (team, count = 24) => {
  const response = await get(`/api/sprint-report/iteration-metrics/${encodeURIComponent(team)}/${count}`);
  if (!response.success) throw new Error(response.message || 'Failed to load iteration metrics');
  return response.data || [];
};

/**
 * Fetch all teams grouped by stream from the Team Management config.
 * Returns: { streamName: [{ id, displayName }], ... }
 */
export const getStreamMap = async () => {
  const response = await getTeamsWithStreams();
  const teams = (response?.data || response || []);
  const map = {};
  teams.forEach(t => {
    if (!t.stream || !t.id) return;
    if (!map[t.stream]) map[t.stream] = [];
    map[t.stream].push({ id: t.id, displayName: t.displayName || t.id });
  });
  return map;
};

/**
 * Fetch and aggregate iteration metrics across all teams in a stream.
 * Sprints are matched by name; SP fields are summed across teams per sprint.
 * Returns the last `count` unique sprints by end date, oldest-first.
 */
export const getStreamIterationMetrics = async (teamIds, count = 24) => {
  // Fetch all teams in parallel — ask for more than `count` so we can align properly
  const allResults = await Promise.all(
    teamIds.map(id => getIterationMetrics(id, count).catch(() => []))
  );

  // Collect all unique sprint names and their earliest known dates
  const sprintMeta = {};
  allResults.forEach(rows => {
    rows.forEach(r => {
      if (!sprintMeta[r.sprintName]) {
        sprintMeta[r.sprintName] = { startDate: r.startDate, endDate: r.endDate };
      }
    });
  });

  // Sort sprint names by end date descending, take last `count`
  const sortedNames = Object.entries(sprintMeta)
    .sort((a, b) => new Date(b[1].endDate) - new Date(a[1].endDate))
    .slice(0, count)
    .map(([name]) => name);

  // Build a lookup: sprintName → aggregated row
  const agg = {};
  sortedNames.forEach(name => {
    agg[name] = {
      sprintName:      name,
      startDate:       sprintMeta[name].startDate,
      endDate:         sprintMeta[name].endDate,
      plannedPoints:   0,
      completedPoints: 0,
      scopeCreepPoints: 0,
      workItemCount:   0,
    };
  });

  allResults.forEach(rows => {
    rows.forEach(r => {
      if (!agg[r.sprintName]) return;
      agg[r.sprintName].plannedPoints    += r.plannedPoints    || 0;
      agg[r.sprintName].completedPoints  += r.completedPoints  || 0;
      agg[r.sprintName].scopeCreepPoints += r.scopeCreepPoints || 0;
      agg[r.sprintName].workItemCount    += r.workItemCount    || 0;
    });
  });

  // Recalculate derived fields from aggregated sums
  const rows = Object.values(agg).map(r => {
    const endScope      = r.plannedPoints + r.scopeCreepPoints;
    const missed        = Math.max(0, endScope - r.completedPoints);
    const completionRate = endScope > 0
      ? Math.round((r.completedPoints / endScope) * 1000) / 10
      : 0;
    return { ...r, missedMovedPoints: missed, endScopePoints: endScope, completionRate };
  });

  // Return chronological order (oldest first)
  return rows.sort((a, b) => new Date(a.endDate) - new Date(b.endDate));
};

// ── Iteration KPI endpoints (new iteration-kpis collection) ──────────────────

/**
 * Fetch stored KPIs for a single team from the iteration-kpis collection.
 */
export const getTeamIterationKpis = async (team, count = 24) => {
  const response = await get(`/api/iteration-kpi/team/${encodeURIComponent(team)}?count=${count}`);
  if (!response.success) throw new Error(response.message || 'Failed to load KPIs');
  return response.data || [];
};

/**
 * Fetch aggregated KPIs across multiple teams from the iteration-kpis collection.
 * @param {string[]} teamIds
 */
export const getAggregatedIterationKpis = async (teamIds, count = 24) => {
  const teamsParam = teamIds.join(',');
  const response = await get(`/api/iteration-kpi/aggregate?teams=${encodeURIComponent(teamsParam)}&count=${count}`);
  if (!response.success) throw new Error(response.message || 'Failed to load aggregated KPIs');
  return response.data || [];
};

/**
 * Fetch the timestamp of the last refresh.
 */
export const getLastRefreshedAt = async () => {
  const response = await get('/api/iteration-kpi/last-refreshed');
  return response.refreshedAt || null;
};

/**
 * Trigger a refresh from ADO for all teams (or a specific list).
 * @param {string[]} [teams]  Leave empty to refresh all default teams.
 * @param {number}   [count]  Number of iterations to fetch (default 24).
 */
export const refreshIterationKpis = async (teams, count = 24) => {
  const body = { count };
  if (teams && teams.length > 0) body.teams = teams;
  const response = await authPost('/api/iteration-kpi/refresh', body);
  if (!response.success) throw new Error(response.message || 'Refresh failed');
  return response;
};

/**
 * Fetch per-sprint PBI / Production Bug / Internal Bug breakdown.
 * @param {string[]} teamIds
 * @param {number}   count
 */
export const getWorkItemBreakdown = async (teamIds, count = 24) => {
  const teamsParam = (teamIds || []).join(',');
  const response = await get(
    `/api/sprint-report/work-item-type-breakdown?teams=${encodeURIComponent(teamsParam)}&count=${count}`
  );
  if (!response.success) throw new Error(response.message || 'Failed to load work item breakdown');
  return response.data || [];
};

/**
 * Fetch bugs CREATED within a sprint's date window for a team (live ADO query,
 * not bound to the sprint's iteration).
 * @returns {Promise<{ total, productionCount, nonProductionCount, bugs }>}
 */
export const getBugsCreatedInSprint = async (sprintName, team) => {
  const response = await get(
    `/api/sprint-report/bugs-created/${encodeURIComponent(sprintName)}/${encodeURIComponent(team)}`
  );
  if (!response.success) throw new Error(response.message || 'Failed to load bugs created in sprint');
  return response.data || { total: 0, productionCount: 0, nonProductionCount: 0, bugs: [] };
};

/**
 * Fetch per-PBI code activity (commit count + develop-branch PR) for a sprint/team.
 * @returns {Promise<{ items: Array }>}
 */
export const getPbiCodeActivity = async (sprintName, team) => {
  const response = await get(
    `/api/sprint-report/pbi-code-activity/${encodeURIComponent(sprintName)}/${encodeURIComponent(team)}`
  );
  if (!response.success) throw new Error(response.message || 'Failed to load PBI code activity');
  return response.data || { items: [] };
};

/**
 * Fetch retrospective boards + feedback for given team names.
 * @param {string[]} teamNames  ADO team names (empty = all teams)
 * @param {number}   months     Look-back window (default 12)
 */
export const getRetrospectives = async (teamNames = [], months = 12) => {
  const teamsParam = teamNames.join(',');
  const url = `/api/retrospectives?months=${months}${teamsParam ? `&teams=${encodeURIComponent(teamsParam)}` : ''}`;
  const response = await authGet(url);
  if (!response.success) throw new Error(response.message || 'Failed to load retrospectives');
  return response.data || [];
};
